/**
 * OfflinePackageModal — FP-037 (2026-09-09 audit remediation).
 *
 * The explicit "download this project for offline" UI: shows the
 * manifest (sheet count, linked-record counts), a storage estimate
 * checked against real device quota, a progress bar during download,
 * and an HONEST completeness/freshness state afterward — never a
 * blanket "available offline" claim when some items failed or the
 * live project has moved on since the last download.
 */
import React, { useEffect, useState } from "react";
import { X, Download, HardDrive, AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import {
  getStorageEstimate, downloadForOffline, getPackageStatus, clearPackage,
} from "@/lib/offlinePackage";

function formatBytes(n) {
  if (!n || n <= 0) return "0 MB";
  const mb = n / 1_000_000;
  return mb >= 1000 ? `${(mb / 1000).toFixed(1)} GB` : `${mb.toFixed(0)} MB`;
}

export default function OfflinePackageModal({ projectId, onClose }) {
  const [manifest, setManifest] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [status, setStatus] = useState(null);
  const [progress, setProgress] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: m }, s, st] = await Promise.all([
          api.get(`/projects/${projectId}/offline_manifest`),
          getStorageEstimate(),
          getPackageStatus(projectId),
        ]);
        if (cancelled) return;
        setManifest(m);
        setEstimate(s);
        setStatus(st);
      } catch (e) {
        if (!cancelled) setLoadError(e?.response?.data?.detail || "Could not load offline manifest");
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  const start = async () => {
    setDownloading(true);
    setProgress({ done: 0, total: 1, failed: 0 });
    try {
      await downloadForOffline(projectId, (p) => setProgress(p));
      const st = await getPackageStatus(projectId);
      setStatus(st);
    } finally {
      setDownloading(false);
    }
  };

  const remove = () => {
    clearPackage(projectId);
    setStatus({ state: "not_downloaded" });
  };

  const quotaWarning = estimate?.supported && manifest
    && (estimate.quotaBytes - estimate.usageBytes) < manifest.estimate.bytes;

  return (
    <div className="fixed inset-0 z-[90] bg-black/60 flex items-center justify-center p-4"
         onClick={onClose} data-testid="offline-package-modal">
      <div className="w-full max-w-md bg-[var(--fop-surface)] border border-[var(--fop-border)] rounded-md"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--fop-border)]">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Download size={16} /> Download for Offline
          </div>
          <button onClick={onClose} data-testid="offline-package-close-btn" className="p-1 text-[var(--fop-text-3)] hover:text-[var(--fop-text-1)]">
            <X size={16} />
          </button>
        </div>

        <div className="p-4 space-y-4 text-sm">
          {loadError && (
            <div className="text-[var(--st-escalated)] text-xs" data-testid="offline-package-error">{loadError}</div>
          )}

          {manifest && (
            <div className="space-y-1" data-testid="offline-package-manifest-summary">
              <div className="text-[var(--fop-text-2)]">
                <b>{manifest.sheets.length}</b> current sheets ·{" "}
                <b>{manifest.linked_record_counts.photos}</b> photos ·{" "}
                <b>{manifest.linked_record_counts.pins}</b> pins ·{" "}
                <b>{manifest.linked_record_counts.punch_items}</b> punch items ·{" "}
                <b>{manifest.linked_record_counts.objects}</b> objects
              </div>
              <div className="flex items-center gap-1.5 text-xs text-[var(--fop-text-3)]">
                <HardDrive size={12} /> Estimated size: {formatBytes(manifest.estimate.bytes)}{" "}
                <span className="opacity-70">({manifest.estimate.basis})</span>
              </div>
              {quotaWarning && (
                <div className="flex items-center gap-1.5 text-xs text-[var(--st-escalated)]" data-testid="offline-package-quota-warning">
                  <AlertTriangle size={12} /> This device may not have enough free storage
                  ({formatBytes(estimate.quotaBytes - estimate.usageBytes)} free)
                  for a full download.
                </div>
              )}
            </div>
          )}

          {status && status.state !== "not_downloaded" && !downloading && (
            <div className="text-xs space-y-1" data-testid="offline-package-status">
              {status.state === "available" && (
                <div className="flex items-center gap-1.5 text-[var(--st-verified)]">
                  <CheckCircle2 size={13} /> Fully available offline ({status.done}/{status.total} items)
                </div>
              )}
              {status.state === "incomplete" && (
                <div className="flex items-center gap-1.5 text-[var(--st-escalated)]">
                  <AlertTriangle size={13} /> Incomplete — {status.failed} item(s) failed to
                  download ({status.done}/{status.total} succeeded). Some content may be
                  missing offline.
                </div>
              )}
              {status.state === "interrupted" && (
                <div className="flex items-center gap-1.5 text-[var(--st-escalated)]" data-testid="offline-package-interrupted">
                  <AlertTriangle size={13} /> Download was interrupted before it finished
                  ({status.done}/{status.total} items saved). Resume the download before
                  relying on this offline — it is NOT fully available yet.
                </div>
              )}
              {status.state === "evicted" && (
                <div className="flex items-center gap-1.5 text-[var(--st-escalated)]" data-testid="offline-package-evicted">
                  <AlertTriangle size={13} /> {status.missingFromCache} previously-downloaded
                  item(s) are no longer in this device's storage. Re-download to restore full
                  offline availability.
                </div>
              )}
              {status.state === "downloaded_unverified" && (
                <div className="flex items-center gap-1.5 text-[var(--fop-text-3)]" data-testid="offline-package-unverified">
                  Downloaded ({status.done}/{status.total} items) — could not confirm this
                  device's offline storage right now.
                </div>
              )}
              {status.state === "stale" && (
                <div className="flex items-center gap-1.5 text-[var(--fop-accent)]">
                  <RefreshCw size={13} /> This project has changed since your last download
                  (new revision or field records). Re-download to update.
                </div>
              )}
              {status.state === "downloaded_freshness_unknown" && (
                <div className="text-[var(--fop-text-3)]">
                  Downloaded ({status.done}/{status.total} items) — freshness could not be
                  checked while offline.
                </div>
              )}
            </div>
          )}

          {downloading && progress && (
            <div className="space-y-1" data-testid="offline-package-progress">
              <div className="h-2 bg-[var(--fop-border)] rounded overflow-hidden">
                <div className="h-full bg-[var(--fop-accent)] transition-all"
                     style={{ width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%` }} />
              </div>
              <div className="text-xs text-[var(--fop-text-3)]">
                {progress.done}/{progress.total} downloaded
                {progress.failed > 0 && ` · ${progress.failed} failed`}
                {progress.persisted === false && (
                  <span className="text-[var(--st-escalated)]"> · could not save progress locally (storage full?)</span>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 pt-1">
            <button
              type="button" onClick={start} disabled={downloading || !manifest}
              className="btn btn-primary inline-flex items-center gap-1.5 text-xs"
              data-testid="offline-package-download-btn"
            >
              <Download size={13} />
              {downloading ? "Downloading…"
                : status?.state === "available" ? "Re-download"
                : status?.state === "incomplete" ? "Retry failed items"
                : status?.state === "interrupted" ? "Resume download"
                : status?.state === "evicted" ? "Re-download"
                : status?.state === "stale" ? "Update download"
                : "Download now"}
            </button>
            {status && status.state !== "not_downloaded" && !downloading && (
              <button
                type="button" onClick={remove}
                className="btn btn-ghost text-xs text-[var(--fop-text-3)]"
                data-testid="offline-package-remove-btn"
              >
                Forget this download
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
